import type {
  ActionCategory,
  Decision,
  Permissions,
  UpdatePermissionsInput,
} from "@typedoc/contracts/connections";

/**
 * Unsaved permission changes, held above the tabs so switching to Activity and back keeps them.
 * Only what differs from the saved settings is stored. `baseRevision` is the revision the person
 * was looking at when the draft first changed, and it is what Save sends: a refetch that brings a
 * newer revision (someone else's save) must not quietly become the base, or the save would
 * overwrite changes nobody reviewed.
 */
export type PermissionsDraft = {
  baseRevision: number | null;
  lookOnly: boolean | null;
  categories: Partial<Record<ActionCategory, Decision>>;
  /** A tool's exception: a decision, or null to clear the one it has. */
  overrides: Record<string, Decision | null>;
};

export const EMPTY_DRAFT: PermissionsDraft = {
  baseRevision: null,
  lookOnly: null,
  categories: {},
  overrides: {},
};

export const isDirty = (draft: PermissionsDraft) =>
  draft.lookOnly !== null ||
  Object.keys(draft.categories).length > 0 ||
  Object.keys(draft.overrides).length > 0;

/** Records the base on the first change and forgets it once nothing is left to save. */
function settle(next: PermissionsDraft, previous: PermissionsDraft, saved: Permissions) {
  if (!isDirty(next)) return EMPTY_DRAFT;
  return { ...next, baseRevision: previous.baseRevision ?? saved.revision };
}

export function setLookOnly(draft: PermissionsDraft, saved: Permissions, value: boolean) {
  return settle({ ...draft, lookOnly: value === saved.lookOnly ? null : value }, draft, saved);
}

export function setCategory(
  draft: PermissionsDraft,
  saved: Permissions,
  key: ActionCategory,
  value: Decision,
) {
  const categories = { ...draft.categories };
  if (value === saved.categories.find((category) => category.key === key)?.decision) {
    delete categories[key];
  } else {
    categories[key] = value;
  }
  return settle({ ...draft, categories }, draft, saved);
}

/**
 * Choosing what the category already says is not an exception; it clears one. `stored` is the
 * exception the tool has now, so choosing it again leaves nothing to save for that tool.
 */
export function setOverride(
  draft: PermissionsDraft,
  saved: Permissions,
  change: { tool: string; stored: Decision | null; value: Decision; inherited: Decision },
) {
  const wanted = change.value === change.inherited ? null : change.value;
  const overrides = { ...draft.overrides };
  if (wanted === change.stored) delete overrides[change.tool];
  else overrides[change.tool] = wanted;
  return settle({ ...draft, overrides }, draft, saved);
}

/**
 * Flipping Use in chat is the person's own change and moves the account's revision on by one. A
 * draft begun on the revision the flip was sent against is still the person's view of the account,
 * so its base follows to the revision the flip returned. Anything else (a clean draft, a draft
 * begun elsewhere, a returned revision that is not exactly one more) means someone else changed
 * the account too, and the base stays so that Save still reports the conflict.
 */
export function rebaseDraft(draft: PermissionsDraft, sent: number, returned: number) {
  if (!isDirty(draft) || draft.baseRevision !== sent || returned !== sent + 1) return draft;
  return { ...draft, baseRevision: returned };
}

/** What Save sends: every category spelled out, against the revision the draft started from. */
export function toSaveInput(draft: PermissionsDraft, saved: Permissions): UpdatePermissionsInput {
  return {
    revision: draft.baseRevision ?? saved.revision,
    lookOnly: draft.lookOnly ?? saved.lookOnly,
    categories: Object.fromEntries(
      saved.categories.map((category) => [
        category.key,
        draft.categories[category.key] ?? category.decision,
      ]),
    ) as Record<ActionCategory, Decision>,
    overrides: draft.overrides,
  };
}
